import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  ArrowLeft, Star, Users, Clock, MapPin, Send, Check,
  Music, Sparkles, PartyPopper, Utensils, Flag, Camera,
  Shirt, UserPlus, Baby, Dumbbell, GraduationCap, Palette, Calendar
} from 'lucide-react';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import axios from 'axios';
import { PricingCalculator } from '@/components/PricingCalculator';
import { BookedDateHint } from '@/components/home/BookedDateHint';
import { getPhoneDisplay, getPhoneTel } from '@/lib/contactInfo';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import { PageEditProvider, usePageEditMode } from '@/context/PageEditModeContext';
import { PageEditToolbar } from '@/components/edit/PageEditToolbar';
import { AdminEditorLink } from '@/components/edit/AdminEditorLink';
import { PageEditable } from '@/components/edit/PageEditable';
import { EditableList } from '@/components/edit/EditableList';

const API = process.env.REACT_APP_BACKEND_URL;

// Performance categories with icons
const PERFORMANCE_TYPES = [
  {
    category: "Parade Performances",
    icon: Flag,
    color: "amber",
    items: [
      { name: "Convention Parades", desc: "Kick off a convention or meeting sessions opening or closing", people: "5-18", duration: "10-30 min", price: 0 },
      { name: "Guidance Parades", desc: "Assists with moving large and small conventioneers from one location to another", people: "5-18", duration: "10-30 min", price: 0 },
      { name: "Energize Parade", desc: "Re-energize convention-goers with a surprise parade during long conference sessions", people: "5-18", duration: "10-30 min", price: 0 },
      { name: "Food & Dessert Delivery", desc: "WOW your guests as Samba Parade leads out waiters with their favorite dish", people: "5-18", duration: "10-30 min", price: 0 },
      { name: "General Samba Parade", desc: "Outdoor and indoor parades - small, medium or large", people: "5-30", duration: "15-60 min", price: 0 },
      { name: "Meet and Greet Parade", desc: "Stand at main entrance as guests enter, then parade to focus attention", people: "5-18", duration: "15-30 min", price: 0 },
    ]
  },
  {
    category: "Stage Shows",
    icon: Sparkles,
    color: "purple",
    items: [
      { name: "Rio de Janeiro Experience", desc: "High energy non-stop show with traditional rhythms, songs, dances and costumes of Brasil", people: "12-20", duration: "30-45 min", price: 0 },
      { name: "New Orleans Rio Experience", desc: "Blending New Orleans Afro-Carnival Culture with Afro-Brasilian carnival", people: "20-35", duration: "30-60 min", price: 0 },
      { name: "The Extravaganza", desc: "Total package of all Brasilian performances in one show - dancers, drummers, full band, stilt walkers", people: "30-35", duration: "30-60 min", price: 0 },
      { name: "Samba Show", desc: "Traditional carnival show with Bateria (percussion band), Passistas (dancers), Pandeiro acrobatics", people: "6-25", duration: "15-60 min", price: 0 },
      { name: "Presentation of the Orixas", desc: "Authentic Afro-Brasilian cultural/religious theatrical presentation", people: "6-25", duration: "15-45 min", price: 0 },
      { name: "Afro-Brasilian Cultural Presentation", desc: "Showcase of various Afro-Brasilian cultural dances, songs, and rhythms", people: "6-25", duration: "60 min+", price: 0 },
    ]
  },
  {
    category: "Interactive Experiences",
    icon: UserPlus,
    color: "green",
    items: [
      { name: "Take a Photo With Us", desc: "Guests take photos with Brasilian headdresses and costumes", people: "2-10", duration: "15-60 min", price: 0 },
      { name: "Custom Design Costumes", desc: "Headdress/mask with your company logo - become part of the entertainment", people: "N/A", duration: "N/A", price: 0 },
      { name: "Perform With Escola de Samba Casa Samba", desc: "Custom Brasilian choreography or drumming piece for your group", people: "10-50", duration: "15-60 min", price: 0 },
      { name: "Team Building Brasilian Style", desc: "Build cohesive work atmosphere through drumming and dance", people: "10-250", duration: "60 min+", price: 0 },
    ]
  },
  {
    category: "Specialty Performers",
    icon: Star,
    color: "yellow",
    items: [
      { name: "The Samba Man", desc: "Mestre Curtis Pierre - Interactive program for K-12 schools and assemblies", people: "Solo", duration: "45 min", price: 0 },
      { name: "Professional Stilt Dancers", desc: "Chakaba/Mocko Jumbie - Acrobatic stilt dancers up to 15 feet tall", people: "1-30", duration: "15-60 min", price: 0 },
      { name: "Children's Stilt Dancers", desc: "Young performers trained in traditional stilt dancing", people: "2-10", duration: "15-30 min", price: 0 },
    ]
  },
  {
    category: "Workshops & Classes",
    icon: GraduationCap,
    color: "blue",
    items: [
      { name: "Samba Dance Class", desc: "Beginner to advanced Brasilian dance instruction", people: "5-50", duration: "60-120 min", price: 0 },
      { name: "Samba Percussion Workshop", desc: "Learn traditional Brasilian rhythms and instruments", people: "5-50", duration: "60-120 min", price: 0 },
      { name: "Drummercize", desc: "Unique exercise program combining drumming and fitness", people: "5-50", duration: "45-60 min", price: 0 },
      { name: "Children's Stilt Classes", desc: "Kids learn to walk and dance on stilts", people: "5-20", duration: "60 min", price: 0 },
    ]
  },
  {
    category: "Custom Services",
    icon: Palette,
    color: "pink",
    items: [
      { name: "Custom Costume Design", desc: "Brasilian and New Orleans style costumes made for your event", people: "N/A", duration: "N/A", price: 0 },
      { name: "Costume Rentals", desc: "Rent authentic Brasilian costumes for your group", people: "1-50", duration: "Per event", price: 0 },
    ]
  }
];

export const BookingPage = () => (
  <PageEditProvider slug="booking">
    <BookingPageInner />
    <PageEditToolbar />
    <AdminEditorLink to="/admin/dashboard#quote-requests-btn" label="Edit Booking" />
  </PageEditProvider>
);

// Standalone Quote page — renders ONLY the Quote Request Form (no pricing calculator, no booking preamble).
// Used by the hero "Get a Quote" button and anywhere else that wants a dedicated quote URL.
export const QuotePage = () => (
  <PageEditProvider slug="quote">
    <div className="min-h-screen bg-background py-20">
      <QuoteRequestForm />
    </div>
    <AdminEditorLink to="/admin/quote-form-editor" label="Edit Quote Form" />
  </PageEditProvider>
);

const BookingPageInner = () => {
  const { executeRecaptcha } = useGoogleReCaptcha();
  const { getValue } = usePageEditMode();
  const [selectedItems, setSelectedItems] = useState([]);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    eventDate: '',
    eventType: '',
    location: '',
    guestCount: '',
    details: ''
  });
  const [submitting, setSubmitting] = useState(false);

  // Effective categories: merge saved overrides (from PageEditProvider) over the
  // hardcoded PERFORMANCE_TYPES so admin-added items are reflected everywhere.
  const savedPerf = getValue('booking.perf');
  const effectiveCategories = PERFORMANCE_TYPES.map((cat, catIdx) => {
    const savedItems = Array.isArray(savedPerf?.[catIdx]?.items) ? savedPerf[catIdx].items : null;
    return { ...cat, items: savedItems && savedItems.length ? savedItems : cat.items };
  });

  const toggleItem = (categoryIndex, itemIndex) => {
    const key = `${categoryIndex}-${itemIndex}`;
    setSelectedItems(prev => 
      prev.includes(key) 
        ? prev.filter(k => k !== key)
        : [...prev, key]
    );
  };

  const isSelected = (categoryIndex, itemIndex) => {
    return selectedItems.includes(`${categoryIndex}-${itemIndex}`);
  };

  const getSelectedItemsDetails = () => {
    return selectedItems.map(key => {
      const [catIdx, itemIdx] = key.split('-').map(Number);
      const cat = effectiveCategories[catIdx];
      const item = cat?.items?.[itemIdx] || {};
      return {
        category: cat?.category || '',
        ...item,
      };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (selectedItems.length === 0) {
      toast.error('Please select at least one performance');
      return;
    }

    setSubmitting(true);
    
    try {
      const selectedPerformances = getSelectedItemsDetails();
      const totalAmount = selectedPerformances.reduce((sum, item) => sum + item.price, 0);
      const recaptchaToken = executeRecaptcha ? await executeRecaptcha('booking') : '';
      
      const bookingRequest = {
        ...formData,
        performances: selectedPerformances,
        total_amount: totalAmount,
        submitted_at: new Date().toISOString(),
        recaptchaToken,
      };
      
      // Send to backend (will save and send email notification)
      const response = await fetch(`${API}/api/bookings`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(bookingRequest)
      });
      
      if (response.ok) {
        toast.success('Booking request submitted! We\'ll contact you within 24 hours.');
        setFormData({
          name: '',
          email: '',
          phone: '',
          eventDate: '',
          eventType: '',
          location: '',
          guestCount: '',
          details: ''
        });
        setSelectedItems([]);
      } else {
        throw new Error('Failed to submit');
      }
    } catch (error) {
      console.error('Booking error:', error);
      toast.error('Failed to submit. Please try again or call us directly.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Hero */}
      <section className="relative py-20 px-6 bg-gradient-to-br from-primary/20 via-background to-purple-900/20">
        <div className="max-w-6xl mx-auto">
          <Link to="/" className="inline-flex items-center gap-2 text-muted-foreground hover:text-primary mb-8 transition-colors">
            <ArrowLeft className="w-5 h-5" />
            Back to Home
          </Link>
          
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center"
          >
            <span className="text-primary font-bold uppercase tracking-widest text-sm mb-4 block">
              <PageEditable path="booking.hero_kicker" as="span" fallback="Book Escola de Samba Casa Samba" />
            </span>
            <h1 className="text-4xl md:text-6xl font-black text-foreground mb-4">
              <PageEditable path="booking.hero_title" as="span" fallback="What We Do Best" />
            </h1>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              <PageEditable
                path="booking.hero_subtitle"
                as="span"
                multiline
                fallback="38+ years of bringing the energy of Brasil to events worldwide. Select your performances and book today."
              />
            </p>
          </motion.div>
        </div>
      </section>

      {/* Performance Selection */}
      <section className="py-16 px-6">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-2xl font-bold text-foreground mb-8 text-center">
            <PageEditable path="booking.select_title" as="span" fallback="Select Your Performances" />
            {selectedItems.length > 0 && (
              <span className="ml-3 text-primary">({selectedItems.length} selected)</span>
            )}
          </h2>

          <div className="space-y-12">
            {effectiveCategories.map((category, catIdx) => (
              <motion.div
                key={catIdx}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: catIdx * 0.1 }}
              >
                {/* Category Header */}
                <div className="flex items-center gap-3 mb-6">
                  <div className={`w-12 h-12 rounded-xl bg-${category.color}-500/20 flex items-center justify-center`}>
                    <category.icon className={`w-6 h-6 text-${category.color}-400`} />
                  </div>
                  <h3 className="text-xl font-bold text-foreground">
                    <PageEditable path={`booking.perf.${catIdx}.category`} as="span" fallback={category.category} />
                  </h3>
                </div>

                {/* Items Grid (add/delete/reorder in edit mode) */}
                <EditableList
                  path={`booking.perf.${catIdx}.items`}
                  fallback={category.items}
                  emptyItem={() => ({ name: 'New performance', desc: 'Describe this performance', people: '5-10', duration: '30 min', price: 0 })}
                  className="grid md:grid-cols-2 lg:grid-cols-3 gap-4"
                  renderItem={(item, itemIdx) => {
                    const selected = isSelected(catIdx, itemIdx);
                    return (
                      <motion.button
                        onClick={() => toggleItem(catIdx, itemIdx)}
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                        className={`w-full h-full text-left p-5 rounded-xl border-2 transition-all ${
                          selected 
                            ? 'border-primary bg-primary/10' 
                            : 'border-border/50 bg-card hover:border-primary/50'
                        }`}
                      >
                        <div className="flex items-start justify-between mb-2">
                          <h4 className={`font-bold ${selected ? 'text-primary' : 'text-foreground'}`}>
                            <PageEditable path={`booking.perf.${catIdx}.items.${itemIdx}.name`} as="span" fallback={item.name} />
                          </h4>
                          <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${
                            selected ? 'border-primary bg-primary' : 'border-muted-foreground'
                          }`}>
                            {selected && <Check className="w-4 h-4 text-primary-foreground" />}
                          </div>
                        </div>
                        <p className="text-sm text-muted-foreground mb-3 line-clamp-2">
                          <PageEditable path={`booking.perf.${catIdx}.items.${itemIdx}.desc`} as="span" multiline fallback={item.desc} />
                        </p>
                        <div className="flex flex-wrap gap-2 text-xs">
                          <span className="px-2 py-1 bg-stone-800 rounded-full text-stone-300">
                            <Users className="w-3 h-3 inline mr-1" />
                            <PageEditable path={`booking.perf.${catIdx}.items.${itemIdx}.people`} as="span" fallback={item.people} />
                          </span>
                          <span className="px-2 py-1 bg-stone-800 rounded-full text-stone-300">
                            <Clock className="w-3 h-3 inline mr-1" />
                            <PageEditable path={`booking.perf.${catIdx}.items.${itemIdx}.duration`} as="span" fallback={item.duration} />
                          </span>
                          <span className="px-2 py-1 bg-primary/20 rounded-full text-primary font-bold">
                            ${item.price > 0 ? Number(item.price).toLocaleString() : '0'}
                          </span>
                        </div>
                      </motion.button>
                    );
                  }}
                />
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Booking Form */}
      <section className="py-16 px-6 bg-card/50">
        <div className="max-w-2xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-10"
          >
            <h2 className="text-3xl font-bold text-foreground mb-4">
              <PageEditable path="booking.form_title" as="span" fallback="Book Your Performance" />
            </h2>
            <p className="text-muted-foreground">
              <PageEditable
                path="booking.form_subtitle"
                as="span"
                multiline
                fallback="Fill out the form below and we'll contact you within 24 hours to confirm availability."
              />
            </p>
          </motion.div>

          {/* Selected Items Summary */}
          {selectedItems.length > 0 && (
            <div className="mb-8 p-4 bg-primary/10 rounded-xl border border-primary/30">
              <h3 className="font-bold text-primary mb-2">Selected Performances:</h3>
              <ul className="text-sm text-foreground space-y-1">
                {getSelectedItemsDetails().map((item, idx) => (
                  <li key={idx} className="flex items-center justify-between">
                    <span className="flex items-center gap-2">
                      <Check className="w-4 h-4 text-primary" />
                      {item.name}
                    </span>
                    <span className="text-primary font-bold">${item.price}</span>
                  </li>
                ))}
              </ul>
              <div className="mt-3 pt-3 border-t border-primary/30 flex justify-between font-bold">
                <span>Estimated Total:</span>
                <span className="text-primary text-lg">
                  ${getSelectedItemsDetails().reduce((sum, item) => sum + item.price, 0).toLocaleString()}
                </span>
              </div>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">Your Name *</label>
                <input
                  type="text"
                  required
                  value={formData.name}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                  className="w-full px-4 py-3 bg-background border border-border rounded-xl focus:outline-none focus:ring-2 focus:ring-primary"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">Email *</label>
                <input
                  type="email"
                  required
                  value={formData.email}
                  onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                  className="w-full px-4 py-3 bg-background border border-border rounded-xl focus:outline-none focus:ring-2 focus:ring-primary"
                />
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">Phone</label>
                <input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
                  className="w-full px-4 py-3 bg-background border border-border rounded-xl focus:outline-none focus:ring-2 focus:ring-primary"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">Event Date *</label>
                <input
                  type="date"
                  required
                  value={formData.eventDate}
                  onChange={(e) => setFormData(prev => ({ ...prev, eventDate: e.target.value }))}
                  className="w-full px-4 py-3 bg-background border border-border rounded-xl focus:outline-none focus:ring-2 focus:ring-primary"
                />
                <BookedDateHint date={formData.eventDate} />
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">Event Type *</label>
                <select
                  required
                  value={formData.eventType}
                  onChange={(e) => setFormData(prev => ({ ...prev, eventType: e.target.value }))}
                  className="w-full px-4 py-3 bg-background border border-border rounded-xl focus:outline-none focus:ring-2 focus:ring-primary"
                >
                  <option value="">Select type...</option>
                  <option value="convention">Convention/Conference</option>
                  <option value="corporate">Corporate Event</option>
                  <option value="wedding">Wedding</option>
                  <option value="birthday">Birthday Party</option>
                  <option value="festival">Festival</option>
                  <option value="school">School Assembly</option>
                  <option value="concert">Concert/Performance</option>
                  <option value="private">Private Party</option>
                  <option value="other">Other</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">Expected Guests</label>
                <input
                  type="number"
                  value={formData.guestCount}
                  onChange={(e) => setFormData(prev => ({ ...prev, guestCount: e.target.value }))}
                  placeholder="Approximate number"
                  className="w-full px-4 py-3 bg-background border border-border rounded-xl focus:outline-none focus:ring-2 focus:ring-primary"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground mb-2">Event Location *</label>
              <input
                type="text"
                required
                value={formData.location}
                onChange={(e) => setFormData(prev => ({ ...prev, location: e.target.value }))}
                placeholder="City, State or Venue"
                className="w-full px-4 py-3 bg-background border border-border rounded-xl focus:outline-none focus:ring-2 focus:ring-primary"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground mb-2">Additional Details</label>
              <textarea
                value={formData.details}
                onChange={(e) => setFormData(prev => ({ ...prev, details: e.target.value }))}
                rows={4}
                placeholder="Tell us more about your event and any special requests..."
                className="w-full px-4 py-3 bg-background border border-border rounded-xl focus:outline-none focus:ring-2 focus:ring-primary resize-none"
              />
            </div>

            <button
              type="submit"
              disabled={submitting || selectedItems.length === 0}
              className="w-full py-4 bg-primary text-primary-foreground font-bold rounded-xl hover:bg-primary/90 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {submitting ? (
                <>
                  <div className="w-5 h-5 border-2 border-primary-foreground border-t-transparent rounded-full animate-spin" />
                  Submitting...
                </>
              ) : (
                <>
                  <Send className="w-5 h-5" />
                  Submit Booking Request ({selectedItems.length} items)
                </>
              )}
            </button>
          </form>

          <p className="text-center text-sm text-muted-foreground mt-6">
            Or call us directly: <a href={getPhoneTel()} className="text-primary font-bold">{getPhoneDisplay()}</a>
          </p>
        </div>
      </section>

      {/* Pricing Calculator Section */}
      <section className="py-16 px-6 bg-gradient-to-br from-green-900/10 via-background to-emerald-900/10">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-8"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
              Estimate Your Event Cost
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Use our pricing calculator to get an instant estimate based on your event needs
            </p>
          </motion.div>
          
          <PricingCalculator />
        </div>
      </section>

      {/* Quote Request Form Section */}
      <QuoteRequestForm />
    </div>
  );
};

// Separate Quote Request Form Component
const QuoteRequestForm = () => {
  const { executeRecaptcha } = useGoogleReCaptcha();
  const [quoteData, setQuoteData] = useState({
    // Contact Information
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    company: '',
    
    // Event Details
    eventName: '',
    eventDate: '',
    eventTime: '',
    eventLocation: '',
    venueType: '',
    expectedGuests: '',
    
    // Service Requirements
    serviceType: [],
    performanceDuration: '',
    numberOfPerformers: '',
    
    // Additional Information
    budget: '',
    howDidYouHear: '',
    specialRequests: '',
    uploadFile: null
  });
  const [submittingQuote, setSubmittingQuote] = useState(false);
  const [quoteSubmitted, setQuoteSubmitted] = useState(false);
  const [formConfig, setFormConfig] = useState(null);

  // Default options (used if no config from database)
  const defaultConfig = {
    serviceOptions: [
      'Samba Parade',
      'Stage Show',
      'Stilt Walkers',
      'Drummers Only',
      'Dancers Only',
      'Full Band',
      'Workshop/Class',
      'Team Building',
      'Photo Op/Meet & Greet',
      'Custom Performance'
    ],
    venueOptions: [
      'Indoor Venue',
      'Outdoor Venue',
      'Convention Center',
      'Hotel/Resort',
      'Private Residence',
      'Restaurant/Bar',
      'Festival/Fair',
      'Corporate Office',
      'School/University',
      'Other'
    ],
    budgetRanges: [
      'Under $1,000',
      '$1,000 - $2,500',
      '$2,500 - $5,000',
      '$5,000 - $10,000',
      '$10,000 - $25,000',
      '$25,000+',
      'Not Sure - Need Quote'
    ],
    durationOptions: [
      '15-30 minutes',
      '30-45 minutes',
      '45-60 minutes',
      '1-2 hours',
      '2+ hours',
      'Full day event'
    ],
    performerOptions: [
      '1-5 (Intimate)',
      '6-12 (Small Group)',
      '13-20 (Medium Group)',
      '21-35 (Large Group)',
      '35+ (Full Production)',
      'Not sure - need recommendation'
    ],
    howDidYouHearOptions: [
      'Google Search',
      'Social Media',
      'Referral',
      'Previous Event',
      'GigSalad/The Bash',
      'Other'
    ]
  };

  // Fetch config on mount
  useEffect(() => {
    const fetchConfig = async () => {
      try {
        const response = await axios.get(`${API}/quote-form-config`);
        if (response.data && Object.keys(response.data).length > 0) {
          setFormConfig({ ...defaultConfig, ...response.data });
        } else {
          setFormConfig(defaultConfig);
        }
      } catch (error) {
        setFormConfig(defaultConfig);
      }
    };
    fetchConfig();
  }, []);

  // Prefill from ?service=... query param (deep-link from Performances page)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const service = params.get('service');
    if (service) {
      setQuoteData(prev => ({
        ...prev,
        specialRequests: prev.specialRequests
          ? prev.specialRequests
          : `Interested in: ${service}`,
      }));
      setTimeout(() => {
        const form = document.getElementById('quote-form');
        if (form) form.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 300);
    }
  }, []);

  // Use config or defaults
  const serviceOptions = formConfig?.serviceOptions || defaultConfig.serviceOptions;
  const venueOptions = formConfig?.venueOptions || defaultConfig.venueOptions;
  const budgetRanges = formConfig?.budgetRanges || defaultConfig.budgetRanges;
  const durationOptions = formConfig?.durationOptions || defaultConfig.durationOptions;
  const performerOptions = formConfig?.performerOptions || defaultConfig.performerOptions;
  const howDidYouHearOptions = formConfig?.howDidYouHearOptions || defaultConfig.howDidYouHearOptions;

  const handleServiceToggle = (service) => {
    setQuoteData(prev => ({
      ...prev,
      serviceType: prev.serviceType.includes(service)
        ? prev.serviceType.filter(s => s !== service)
        : [...prev.serviceType, service]
    }));
  };

  const handleQuoteSubmit = async (e) => {
    e.preventDefault();
    setSubmittingQuote(true);

    try {
      const recaptchaToken = executeRecaptcha ? await executeRecaptcha('quote_request') : '';
      const response = await fetch(`${API}/api/quote-requests`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...quoteData,
          submitted_at: new Date().toISOString(),
          recaptchaToken,
        })
      });

      if (response.ok) {
        setQuoteSubmitted(true);
        toast.success('Quote request submitted! We\'ll contact you within 24-48 hours.');
      } else {
        throw new Error('Failed to submit');
      }
    } catch (error) {
      console.error('Quote submission error:', error);
      toast.error('Failed to submit. Please refresh and try again.');
    } finally {
      setSubmittingQuote(false);
    }
  };

  if (quoteSubmitted) {
    return (
      <section className="py-16 px-6 bg-gradient-to-br from-green-900/20 via-background to-primary/10">
        <div className="max-w-2xl mx-auto text-center">
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            className="w-24 h-24 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-6"
          >
            <Check className="w-12 h-12 text-green-500" />
          </motion.div>
          <h2 className="text-3xl font-bold text-foreground mb-4">Quote Request Received!</h2>
          <p className="text-muted-foreground mb-6">
            Thank you for your interest in Escola de Samba Casa Samba! We've received your quote request and will contact you at <strong className="text-primary">{quoteData.email}</strong> within 24-48 hours.
          </p>
          <p className="text-sm text-muted-foreground">
            Need immediate assistance? Call us at <a href={getPhoneTel()} className="text-primary font-bold">{getPhoneDisplay()}</a>
          </p>
        </div>
      </section>
    );
  }

  return (
    <section className="py-16 px-6 bg-gradient-to-br from-purple-900/20 via-background to-primary/10" id="quote-form">
      <div className="max-w-4xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-10"
        >
          <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
            Request a Detailed Quote
          </h2>
          <p className="text-muted-foreground max-w-2xl mx-auto">
            Tell us about your event and we'll create a customized quote for your Brasilian entertainment needs
          </p>
        </motion.div>

        <form onSubmit={handleQuoteSubmit} className="bg-card border border-border/50 rounded-2xl p-8 shadow-xl space-y-8">
          
          {/* Section 1: Contact Information */}
          <div className="space-y-6">
            <h3 className="text-xl font-bold text-foreground border-b border-border pb-3 flex items-center gap-2">
              <Users className="w-5 h-5 text-primary" />
              Contact Information
            </h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">First Name <span className="text-red-500">*</span></label>
                <input
                  type="text"
                  required
                  value={quoteData.firstName}
                  onChange={(e) => setQuoteData(prev => ({ ...prev, firstName: e.target.value }))}
                  className="w-full px-4 py-3 bg-background border border-border rounded-xl focus:outline-none focus:ring-2 focus:ring-primary"
                  placeholder="Your first name"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">Last Name <span className="text-red-500">*</span></label>
                <input
                  type="text"
                  required
                  value={quoteData.lastName}
                  onChange={(e) => setQuoteData(prev => ({ ...prev, lastName: e.target.value }))}
                  className="w-full px-4 py-3 bg-background border border-border rounded-xl focus:outline-none focus:ring-2 focus:ring-primary"
                  placeholder="Your last name"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">Email Address <span className="text-red-500">*</span></label>
                <input
                  type="email"
                  required
                  value={quoteData.email}
                  onChange={(e) => setQuoteData(prev => ({ ...prev, email: e.target.value }))}
                  className="w-full px-4 py-3 bg-background border border-border rounded-xl focus:outline-none focus:ring-2 focus:ring-primary"
                  placeholder="your@email.com"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">Phone Number <span className="text-red-500">*</span></label>
                <input
                  type="tel"
                  required
                  value={quoteData.phone}
                  onChange={(e) => setQuoteData(prev => ({ ...prev, phone: e.target.value }))}
                  className="w-full px-4 py-3 bg-background border border-border rounded-xl focus:outline-none focus:ring-2 focus:ring-primary"
                  placeholder="(555) 123-4567"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground mb-2">Company/Organization</label>
              <input
                type="text"
                value={quoteData.company}
                onChange={(e) => setQuoteData(prev => ({ ...prev, company: e.target.value }))}
                className="w-full px-4 py-3 bg-background border border-border rounded-xl focus:outline-none focus:ring-2 focus:ring-primary"
                placeholder="Your company or organization name"
              />
            </div>
          </div>

          {/* Section 2: Event Details */}
          <div className="space-y-6">
            <h3 className="text-xl font-bold text-foreground border-b border-border pb-3 flex items-center gap-2">
              <Calendar className="w-5 h-5 text-primary" />
              Event Details
            </h3>

            <div>
              <label className="block text-sm font-medium text-foreground mb-2">Event Name/Type <span className="text-red-500">*</span></label>
              <input
                type="text"
                required
                value={quoteData.eventName}
                onChange={(e) => setQuoteData(prev => ({ ...prev, eventName: e.target.value }))}
                className="w-full px-4 py-3 bg-background border border-border rounded-xl focus:outline-none focus:ring-2 focus:ring-primary"
                placeholder="e.g., Corporate Gala, Wedding Reception, Festival"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">Event Date <span className="text-red-500">*</span></label>
                <input
                  type="date"
                  required
                  value={quoteData.eventDate}
                  onChange={(e) => setQuoteData(prev => ({ ...prev, eventDate: e.target.value }))}
                  className="w-full px-4 py-3 bg-background border border-border rounded-xl focus:outline-none focus:ring-2 focus:ring-primary"
                />
                <BookedDateHint date={quoteData.eventDate} />
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">Event Time</label>
                <input
                  type="time"
                  value={quoteData.eventTime}
                  onChange={(e) => setQuoteData(prev => ({ ...prev, eventTime: e.target.value }))}
                  className="w-full px-4 py-3 bg-background border border-border rounded-xl focus:outline-none focus:ring-2 focus:ring-primary"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground mb-2">Event Location <span className="text-red-500">*</span></label>
              <input
                type="text"
                required
                value={quoteData.eventLocation}
                onChange={(e) => setQuoteData(prev => ({ ...prev, eventLocation: e.target.value }))}
                className="w-full px-4 py-3 bg-background border border-border rounded-xl focus:outline-none focus:ring-2 focus:ring-primary"
                placeholder="City, State or Full Address"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">Venue Type</label>
                <select
                  value={quoteData.venueType}
                  onChange={(e) => setQuoteData(prev => ({ ...prev, venueType: e.target.value }))}
                  className="w-full px-4 py-3 bg-background border border-border rounded-xl focus:outline-none focus:ring-2 focus:ring-primary"
                >
                  <option value="">Select venue type...</option>
                  {venueOptions.map(venue => (
                    <option key={venue} value={venue}>{venue}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">Expected Guests</label>
                <input
                  type="number"
                  value={quoteData.expectedGuests}
                  onChange={(e) => setQuoteData(prev => ({ ...prev, expectedGuests: e.target.value }))}
                  className="w-full px-4 py-3 bg-background border border-border rounded-xl focus:outline-none focus:ring-2 focus:ring-primary"
                  placeholder="Approximate number"
                />
              </div>
            </div>
          </div>

          {/* Section 3: Service Requirements */}
          <div className="space-y-6">
            <h3 className="text-xl font-bold text-foreground border-b border-border pb-3 flex items-center gap-2">
              <Music className="w-5 h-5 text-primary" />
              Service Requirements
            </h3>

            <div>
              <label className="block text-sm font-medium text-foreground mb-3">What services are you interested in? <span className="text-red-500">*</span></label>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {serviceOptions.map(service => (
                  <button
                    key={service}
                    type="button"
                    onClick={() => handleServiceToggle(service)}
                    className={`px-4 py-3 rounded-xl border text-sm font-medium transition-all ${
                      quoteData.serviceType.includes(service)
                        ? 'bg-primary text-primary-foreground border-primary'
                        : 'bg-background border-border text-foreground hover:border-primary/50'
                    }`}
                  >
                    {quoteData.serviceType.includes(service) && <Check className="w-4 h-4 inline mr-1" />}
                    {service}
                  </button>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">Performance Duration</label>
                <select
                  value={quoteData.performanceDuration}
                  onChange={(e) => setQuoteData(prev => ({ ...prev, performanceDuration: e.target.value }))}
                  className="w-full px-4 py-3 bg-background border border-border rounded-xl focus:outline-none focus:ring-2 focus:ring-primary"
                >
                  <option value="">Select duration...</option>
                  {durationOptions.map(duration => (
                    <option key={duration} value={duration}>{duration}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">Number of Performers Needed</label>
                <select
                  value={quoteData.numberOfPerformers}
                  onChange={(e) => setQuoteData(prev => ({ ...prev, numberOfPerformers: e.target.value }))}
                  className="w-full px-4 py-3 bg-background border border-border rounded-xl focus:outline-none focus:ring-2 focus:ring-primary"
                >
                  <option value="">Select...</option>
                  {performerOptions.map(option => (
                    <option key={option} value={option}>{option}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {/* Section 4: Budget & Additional Info */}
          <div className="space-y-6">
            <h3 className="text-xl font-bold text-foreground border-b border-border pb-3 flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-primary" />
              Additional Information
            </h3>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">Budget Range</label>
                <select
                  value={quoteData.budget}
                  onChange={(e) => setQuoteData(prev => ({ ...prev, budget: e.target.value }))}
                  className="w-full px-4 py-3 bg-background border border-border rounded-xl focus:outline-none focus:ring-2 focus:ring-primary"
                >
                  <option value="">Select budget range...</option>
                  {budgetRanges.map(range => (
                    <option key={range} value={range}>{range}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">How did you hear about us?</label>
                <select
                  value={quoteData.howDidYouHear}
                  onChange={(e) => setQuoteData(prev => ({ ...prev, howDidYouHear: e.target.value }))}
                  className="w-full px-4 py-3 bg-background border border-border rounded-xl focus:outline-none focus:ring-2 focus:ring-primary"
                >
                  <option value="">Select...</option>
                  {howDidYouHearOptions.map(option => (
                    <option key={option} value={option}>{option}</option>
                  ))}
                </select>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground mb-2">Special Requests or Additional Details</label>
              <textarea
                value={quoteData.specialRequests}
                onChange={(e) => setQuoteData(prev => ({ ...prev, specialRequests: e.target.value }))}
                rows={4}
                className="w-full px-4 py-3 bg-background border border-border rounded-xl focus:outline-none focus:ring-2 focus:ring-primary resize-none"
                placeholder="Tell us more about your vision for the event, any specific songs, themes, or requirements..."
              />
            </div>
          </div>

          {/* Submit Button */}
          <div className="pt-4">
            <button
              type="submit"
              disabled={submittingQuote || quoteData.serviceType.length === 0}
              className="w-full py-4 bg-gradient-to-r from-primary to-orange-500 text-primary-foreground font-bold text-lg rounded-xl hover:from-primary/90 hover:to-orange-400 transition-all disabled:opacity-50 flex items-center justify-center gap-2 shadow-lg"
            >
              {submittingQuote ? (
                <>
                  <div className="w-5 h-5 border-2 border-primary-foreground border-t-transparent rounded-full animate-spin" />
                  Submitting Request...
                </>
              ) : (
                <>
                  <Send className="w-5 h-5" />
                  Submit Quote Request
                </>
              )}
            </button>
            
            <p className="text-center text-sm text-muted-foreground mt-4">
              We typically respond within 24-48 hours. For urgent inquiries, call <a href={getPhoneTel()} className="text-primary font-bold">{getPhoneDisplay()}</a>
            </p>
          </div>
        </form>
      </div>
    </section>
  );
};

export default BookingPage;
